import { useEffect, useState } from "react";
import { logger } from "@/utils/logger";
import { decodeJwtPayload, getStoredProductName } from "@/utils/product";
import { PRODUCT_LEXI_SOCIAL } from "@/utils/vars";

/**
 * Reads the stored session: true for Lexi Pro (or any paid product), false
 * for Lexi Social, no session, or an unreadable token.
 */
export const readIsPaidUser = () => {
  // In dev, always treat user as paid so pro features are testable locally
  if (import.meta.env.VITE_APP_WORKSPACE === "dev") {
    return true;
  }

  try {
    const token = localStorage.getItem("authToken");
    if (!token) {
      // No token means free user
      return false;
    }
    const payload = decodeJwtPayload(token);
    const productName = getStoredProductName();

    // User is NOT paid if they are on Lexi Social (free tier)
    const isLexiSocial =
      (productName || "").toLowerCase() === PRODUCT_LEXI_SOCIAL.toLowerCase() ||
      (payload?.type === "prospect" && !productName);

    return !isLexiSocial;
  } catch (error) {
    logger.error("Error checking user payment status:", error);
    return false; // Default to free user on error
  }
};

/**
 * Custom hook to check if the current user is a paid user
 *
 * The first render already has the right answer. (It used to start as true
 * and correct itself in an effect, so every free or signed-out visitor's
 * first render started the paid-only Early Mail requests on /home, for three
 * days of tips they could not see.)
 * @returns {boolean} isPaidUser - true if user is paid, false if user is on free tier (Lexi Social)
 */
export const usePaidUser = () => {
  const [isPaidUser, setIsPaidUser] = useState(readIsPaidUser);

  useEffect(() => {
    setIsPaidUser(readIsPaidUser());
  }, []);

  return isPaidUser;
};
